/**
 * The caret a block the creator has just added owes them.
 *
 * Adding a block used to hand the creator an empty card and nothing else. The
 * block is brought into view (see the studio's reveal), but its first field
 * still has to be found and tapped before a single character can be typed — a
 * reach across the card for the one thing the studio, having just created the
 * block, already knows the identity of.
 *
 * What the studio does *not* know is which control inside the block the caret
 * belongs in: that is the editor's business, and it differs by block type — a
 * quote's passage, a callout's label, a table's first heading, a formula's
 * source, a question's prompt. So the two halves are joined here instead of by a
 * prop threaded through every block editor and the handful of fields that are
 * not editors at all:
 *
 *   - the studio wraps the new block's editor in `FirstFieldFocus`, telling it
 *     whether this block is the one that was just added;
 *   - every field that could be a block's first asks `useStudioFirstField()`
 *     for the caret as it mounts.
 *
 * The first field to ask wins, and that is exactly the intent: React runs layout
 * effects from the leaves up and left to right, so "first to ask" is "first on
 * the card". Every later field is refused, so a table's fifth cell or a
 * question's third option can never pull the caret out from under the writing.
 *
 * A field with no block around it — the cover's title, the page's own name — has
 * no provider above it, and asking there is a no-op by default, so none of this
 * reaches any other part of the studio.
 */
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
} from 'react'
import {type TextInput} from 'react-native'

/**
 * Asks for the caret on behalf of the field that calls it.
 *
 * `true` means this field is the one — the caller should take the caret. `false`
 * means the question is not its to answer: it is not part of a block that was
 * just added, or another field in that block has already been given the caret.
 */
const Claim = createContext<() => boolean>(() => false)

/**
 * Ask for the caret of a block the creator has just added.
 *
 * Exported because not every field is a `StudioTextField`: two of the studio's
 * fields are plain `TextInput`s (a table's first cell, a callout's label) and
 * take the caret the same way.
 */
export function useStudioFirstField(): () => boolean {
  return useContext(Claim)
}

/**
 * Puts the caret in `target` if this field is the first one of a block the
 * creator has just added, and does nothing whatsoever otherwise.
 *
 * `enabled` is how a field that is only *sometimes* first declines to ask —
 * inside a table every cell is the same component, and exactly one of them is
 * the first. A disabled field never asks at all, so it cannot consume the grant
 * on behalf of the cell that should have had it.
 */
export function useFirstFieldFocus(
  target: RefObject<TextInput | null>,
  enabled = true,
): void {
  const claim = useStudioFirstField()
  useLayoutEffect(() => {
    if (!enabled || !claim()) return
    target.current?.focus()
  }, [claim, enabled, target])
}

/**
 * Marks the block underneath as the one that was just added, for as long as that
 * is true.
 *
 * `active` is the studio's answer to "is this the block the creator just made?",
 * and it is passed down rather than read from a context so that a card which is
 * *not* the new one costs nothing: its fields ask once at mount, are refused,
 * and are never asked again.
 */
export function FirstFieldFocus({
  active,
  onSettled,
  children,
}: {
  /** Whether the block underneath is the one the creator has just added. */
  active: boolean
  /**
   * Called once the block has had its chance — the moment the studio can forget
   * that this block was new.
   */
  onSettled: () => void
  children: ReactNode
}) {
  /** One grant per activation, so a second field can never also be served. */
  const granted = useRef(false)
  const claim = useCallback(() => {
    if (!active || granted.current) return false
    granted.current = true
    return true
  }, [active])

  /*
   * The block is written off as settled once it is active, whether or not one of
   * its fields took the caret.
   *
   * A parent's layout effect runs after its children's, so by the time this runs
   * every field in the new block has asked and the first has been served. Doing
   * the writing-off here — rather than from the field that was served — is what
   * stops the caret coming back later: without it, a block whose editor is
   * remounted (the creator previewing it and coming back) would still look
   * freshly added, and the caret would jump out of wherever they had put it.
   *
   * Writing the block off is also what makes the studio's own record of "new"
   * short-lived, so a block cannot be looked on as new twice.
   */
  useLayoutEffect(() => {
    if (!active) {
      granted.current = false
      return
    }
    onSettled()
  }, [active, onSettled])

  return <Claim.Provider value={claim}>{children}</Claim.Provider>
}
